// i18next wiring for the app. Wording, namespaces and the placeholder syntax come from @aiax-hub/i18n:
//   {name} parameters, English plurals {n|one|other}, numbers grouped (decision D-W01 in docs/dev/DECISIONS.md). Keys are flat ('nav.home') and
//   unique across namespaces, so t(key) works from any component without naming a namespace.
import i18next, { type FormatterModule, type i18n as I18n } from 'i18next';
import { useContext, useMemo } from 'react';
import { DisplayPreferencesContext } from '../lib/display-preferences';
import { DisplayTimezoneContext } from '../lib/display-timezone';
import { initReactI18next, useTranslation } from 'react-i18next';
import { LABELS, i18nextInitOptions, mockupFormatter, type Lang } from '@aiax-hub/i18n';
import { createFmt, type Fmt, type TFn } from '../lib/format';

export type { Lang, TFn };

export function createI18n(lang: Lang): I18n {
  const inst = i18next.createInstance();
  // mockupFormatter returns undefined for null parameters on purpose (placeholder kept) — wider than i18next's type
  inst.use(mockupFormatter as unknown as FormatterModule).use(initReactI18next);
  void inst.init(i18nextInitOptions(lang));
  return inst;
}

/** t(key, params) with the mockup syntax. Unknown keys return the key (like the mockup App.t). */
export function useT(): TFn {
  const { t } = useTranslation();
  return t as unknown as TFn;
}

/** Current wording language (follows the view store through LanguageSync). */
export function useLang(): Lang {
  const { i18n } = useTranslation();
  return i18n.language === 'en' ? 'en' : 'ko';
}

/** Number/time formatting for the current language (App.fmt). */
export function useFmt(numberUnitOverride?: 'compact' | 'full'): Fmt {
  const t = useT();
  const lang = useLang();
  const timezone = useContext(DisplayTimezoneContext);
  const prefs = useContext(DisplayPreferencesContext);
  const numberUnit = numberUnitOverride ?? prefs.numberUnit;
  return useMemo(() => createFmt(lang, t, Date.now, timezone, numberUnit), [lang, t, timezone, numberUnit]);
}

/** Does a key exist (App.hasT)? */
export function useHasT(): (key: string) => boolean {
  const { i18n } = useTranslation();
  return (key: string) => i18n.exists(key);
}

/** Label dictionary text (App.label): 'label.<key>' in the current language. */
export function useLabel(): (key: string) => string {
  const t = useT();
  return (key: string) => t('label.' + key);
}
export const labelSlug = (key: string): string | undefined => LABELS[key]?.slug;
